// store/tournaments.normalize.js
// Adapts the raw BE response (nested under page/tournament, prize tiers as ranges + promo_codes,
// ranking rows with only user_id) into the flat shape TournamentPanel / TournamentPromoPage /
// LeaderboardFab actually consume. Keeping this in one place means BE's shape can keep changing
// without touching the components.

function formatPromoCode(code) {
  // TODO: confirm the real display rules with product/BE — this is a best-effort guess
  // at turning { type: 'BET_FREE_BET_WITH_FIXED_AMOUNT_', origin: 'bet', amount: 10 } into text.
  if (code.type && code.type.includes('FREE_BET')) return `${code.amount}€ Free Bet`
  return `€${code.amount}`
}

function formatPrizeTier(tier) {
  const position = tier.rank_from === tier.rank_to ? `${tier.rank_from}º` : `${tier.rank_from}º-${tier.rank_to}º`
  const amount = (tier.promo_codes || []).map(formatPromoCode).join(' + ')
  return { position, amount }
}

// Rows 4+ have a tiny inline slot with no room for the prize type, so they show just the amount.
function formatShortPromoCode(code) {
  return `${code.amount} €`
}

// Prize for one specific rank: find the tier whose rank_from..rank_to range contains it.
// Top 3 (podium rows) show the full label, e.g. "€10 Free Bet"; ranks 4+ show only "5 €".
// Returns '' when no tier covers that rank (PodiumRow/RankingRow hide the prize when empty).
function prizeForRank(rank, tiers) {
  const tier = (tiers || []).find((t) => rank >= t.rank_from && rank <= t.rank_to)
  if (!tier) return ''
  const format = rank <= 3 ? formatPromoCode : formatShortPromoCode
  return (tier.promo_codes || []).map(format).join(' + ')
}

// Shown instead of the username for the logged-in player's own row
const SELF_LABEL = 'Tú'

function maskUsername(username) {
  if (!username) return username
  return `${username.slice(0, 4)}*****`
}

function normalizeRankingRow(row, currentUserId, tiers) {
  const isSelf = currentUserId != null && row.user_id === currentUserId
  const rawName = row.username || `Jugador ${row.rank}`
  return {
    position: row.rank,
    name: isSelf ? SELF_LABEL : maskUsername(rawName),
    points: `${row.points} points`,
    prize: prizeForRank(row.rank, tiers)
  }
}

function getExtraContent(page, key) {
  const entry = ((page && page.page_extra_content) || []).find((e) => e.key === key)
  return entry ? entry.content : ''
}

function computeOptedIn(pageId, user) {
  if (process.server) return false
  if (!user || !user.opted_in_promotions) return false
  return user.opted_in_promotions.some((p) => p.page_id === pageId)
}

export function normalizeTournament(raw, currentUser) {
  const t = raw.tournament || {}
  const page = raw.page || {}
  const media = raw.media || {}

  return {
    id: raw.id,
    pageId: page.id, // needed for the opt-in POST + for matching opted_in_promotions
    title: raw.title,
    bannerImage: media.background_desktop || media.banner_popup || media.thumbnail || null,
    description: page.content || page.meta?.description || '',
    // Prizes tab has its own dedicated description, keyed inside page_extra_content, separate
    // from the generic `description` above — it's HTML, rendered with v-html.
    prizesDescription: getExtraContent(page, 'prizes_content'),
    // TODO: confirm field — content is null in the sample payload
    rulesParagraphs: page.terms_and_conditions_content ? [page.terms_and_conditions_content] : [],
    startsAt: page.active_from,
    endsAt: page.active_until,
    optedIn: computeOptedIn(page.id, currentUser),
    // NOT in the payload — needed for the "markets" tab and the promo page's eligibility section.
    eligibleSports: raw.eligibleSports || [],
    eligibleLeagues: raw.eligibleLeagues || [],
    eligibleBetTypes: raw.eligibleBetTypes || [],
    prizesTable: (t.tournament_prize_tiers || []).map(formatPrizeTier),
    leaderboardSize: t.leaderboard_size,
    playerCount: t.player_counts,
    leaderboard: (t.tournament_ranking || []).map((row) =>
      normalizeRankingRow(row, t.tournament_user && t.tournament_user.user_id, t.tournament_prize_tiers)
    ),
    currentUser: t.tournament_user
      ? {
          position: t.tournament_user.rank,
          name: SELF_LABEL,
          points: `${t.tournament_user.points} points`,
          prize: prizeForRank(t.tournament_user.rank, t.tournament_prize_tiers)
        }
      : null,
    // Flat convenience fields for the FAB, which shouldn't need to know the currentUser shape
    userPosition: t.tournament_user ? t.tournament_user.rank : null,
    userPoints: t.tournament_user ? t.tournament_user.points : null
  }
}